/**
 * Fills in a generated snippet's placeholders and lays it out as lines of
 * classed pieces, which <Snippet> turns into elements and copyable text.
 *
 * This file has no runtime imports and only uses TypeScript that can be
 * stripped, so Node runs it as it is: scripts/snippets/snippet-render.test.mjs
 * tests it without a build step. Keep it that way (no enums, no imports of
 * values).
 */

import type {
  SnippetFilter,
  SnippetHole,
  SnippetQuote,
  SnippetVariant,
  SnippetVarValues,
} from '../types/snippet';

/**
 * A run of text with a class index (see src/components/Snippet.tsx), or null
 * for plain text
 */
export type SnippetPiece = [className: number | null, text: string];

// Class indexes, as in scripts/snippets/scope-rules.mjs
const KEYWORD = 1;
const STRING = 2;
const NUMBER = 3;
const PUNCTUATION = 5;

const INDENT = '  ';

const IDENTIFIER = /^[A-Za-z_$][A-Za-z0-9_$]*$/;

/**
 * Escape a value for the kind of string literal its placeholder sits in
 */
export const ESCAPES: Record<SnippetQuote, (text: string) => string> = {
  single: text => text.replace(/[\\']/g, '\\$&').replace(/\r?\n/g, '\\n'),
  double: text => text.replace(/[\\"]/g, '\\$&').replace(/\r?\n/g, '\\n'),
  template: text => text.replace(/[\\`]|\$\{/g, match => `\\${match}`),
  'shell-single': text => text.replace(/'/g, `'\\''`),
  'shell-double': text => text.replace(/[\\"$`]/g, '\\$&'),
};

/**
 * Lay out a value as JSON, or as a JS object literal (unquoted keys where
 * possible, single quotes, trailing commas). Objects and arrays put each entry
 * on its own line, and every line after the first starts with `indent`
 */
export function formatValue(
  value: unknown,
  filter: SnippetFilter,
  indent: string
): SnippetPiece[] {
  const pieces: SnippetPiece[] = [];
  const quote = (text: string) =>
    filter === 'js' ? `'${ESCAPES.single(text)}'` : JSON.stringify(text);

  const write = (current: unknown, depth: number): void => {
    const inner = `\n${indent}${INDENT.repeat(depth + 1)}`;
    const outer = `\n${indent}${INDENT.repeat(depth)}`;

    if (current === null) {
      pieces.push([KEYWORD, 'null']);
    } else if (typeof current === 'boolean') {
      pieces.push([KEYWORD, String(current)]);
    } else if (typeof current === 'number') {
      pieces.push([NUMBER, String(current)]);
    } else if (typeof current === 'string') {
      pieces.push([STRING, quote(current)]);
    } else if (Array.isArray(current)) {
      if (current.length === 0) {
        pieces.push([null, '[]']);
        return;
      }
      pieces.push([null, '[']);
      current.forEach((item, i) => {
        pieces.push([null, inner]);
        write(item, depth + 1);
        if (i < current.length - 1 || filter === 'js') {
          pieces.push([PUNCTUATION, ',']);
        }
      });
      pieces.push([null, `${outer}]`]);
    } else {
      const entries = Object.entries(current as Record<string, unknown>);
      if (entries.length === 0) {
        pieces.push([null, '{}']);
        return;
      }
      pieces.push([null, '{']);
      entries.forEach(([key, item], i) => {
        pieces.push([null, inner]);
        if (filter === 'js' && IDENTIFIER.test(key)) {
          pieces.push([null, key]);
        } else {
          pieces.push([STRING, quote(key)]);
        }
        pieces.push([PUNCTUATION, ':'], [null, ' ']);
        write(item, depth + 1);
        if (i < entries.length - 1 || filter === 'js') {
          pieces.push([PUNCTUATION, ',']);
        }
      });
      pieces.push([null, `${outer}}`]);
    }
  };

  // Round-trip through JSON, so the value is laid out the way it would be sent
  // (dates become strings, undefined properties disappear)
  const serialised = JSON.stringify(value);
  write(serialised === undefined ? null : JSON.parse(serialised), 0);

  return pieces;
}

function isVisible(
  conditions: string[] | undefined,
  flags: Record<string, boolean>
) {
  return (conditions ?? []).every(condition =>
    condition.startsWith('!') ? !flags[condition.slice(1)] : !!flags[condition]
  );
}

/**
 * The lines of a snippet variant with its placeholders filled in. Neighbouring
 * pieces with the same class are merged, and a multi-line value (from a json
 * variable) becomes several lines
 */
export function renderSnippetLines(
  variant: SnippetVariant,
  values: SnippetVarValues,
  flags: Record<string, boolean>,
  uuid: (key: string) => string
): SnippetPiece[][] {
  const lines: SnippetPiece[][] = [];

  for (const line of variant.lines) {
    if (!isVisible(line.if, flags)) {
      continue;
    }

    let current: SnippetPiece[] = [];
    lines.push(current);

    const append = (className: number | null, text: string) => {
      text.split('\n').forEach((part, i) => {
        if (i > 0) {
          current = [];
          lines.push(current);
        }
        if (!part) {
          return;
        }
        const previous = current[current.length - 1];
        if (previous && previous[0] === className) {
          previous[1] += part;
        } else {
          current.push([className, part]);
        }
      });
    };

    const fill = (hole: SnippetHole) => {
      const className = hole.c ?? null;
      const value = hole.v.startsWith('uuid:')
        ? uuid(hole.v.slice('uuid:'.length))
        : values[hole.v];

      if (value === undefined) {
        console.warn(`Snippet variable "${hole.v}" has no value`);
        append(className, `{{${hole.v}}}`);
        return;
      }

      const lineText = current.map(([, text]) => text).join('');

      if (hole.f && hole.q) {
        // Inside a string literal (e.g. a cURL body), the value is one string,
        // and its lines line up with the placeholder
        const column = lineText.replace(/[^\t]/g, ' ');
        const text = formatValue(value, hole.f, column)
          .map(([, pieceText]) => pieceText)
          .join('');
        append(className, ESCAPES[hole.q](text));
      } else if (hole.f) {
        // In code, the value is highlighted, and its lines line up with the
        // line the placeholder is on
        const lineIndent = lineText.match(/^[ \t]*/)![0];
        for (const [pieceClass, text] of formatValue(
          value,
          hole.f,
          lineIndent
        )) {
          append(pieceClass, text);
        }
      } else {
        const text = String(value);
        append(className, hole.q ? ESCAPES[hole.q](text) : text);
      }
    };

    for (const token of line.t) {
      if (typeof token === 'string') {
        append(null, token);
      } else if (Array.isArray(token)) {
        append(token[0], token[1]);
      } else {
        fill(token);
      }
    }
  }

  return lines;
}
